import { T, useTranslate } from '@tolgee/react';
import { Col, Row, Tooltip } from 'antd';
import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import styled, { css } from 'styled-components';

import FullScreenSvg from '../../../assets/icons/FullScreenSvg';
import ProfileChecked from '../../../assets/icons/ProfileChecked';
import theme from '../../../assets/theme';
import useApiResponses from '../../../hooks/useApiResponses';
import useAutoMarkAsPlayedResultRound from '../../../hooks/useAutoMarkAsPlayedResultRound';
import useListAllAiAssessedScores from '../../../hooks/useListAllAiAssessedScores';
import { useMetrics } from '../../../hooks/useMetrics';
import useResponses from '../../../hooks/useResponses';
import { useStrengthReviewClickerRegistration } from '../../../hooks/useStrengthReviewClickerRegistration';
import useSwipeResponses from '../../../hooks/useSwipeResponses';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { LiveActionsDisabledContext } from '../../../providers/LiveActionsDisabledProvider/LiveActionsDisabledProvider';
import { Response } from '../../../services/backendService/types';
import { hasScorePromptOutput } from '../../../utils/aiFeedbackScore';
import { makeGridColumns } from '../../../utils/makeGridColumns';
import { zIndexes } from '../../../utils/zIndexes';
import { Loader } from '../../_atoms/_Loader';
import FullScreenHoverNudge from '../../_atoms/FullScreenHoverNudge';
import { IconWrapper } from '../../_atoms/IconWrapper';
import AIResponseReviewView from '../../_molecules/AIResponseReviewView';
import HideResponseToggle from '../../_molecules/HideResponseToggle';
import LazyDrawingResponse from '../../_molecules/LazyDrawingResponse';
import LazyVideoResponse from '../../_molecules/LazyVideoResponse';
import LiveEmptyState from '../../_molecules/LiveEmptyState';
import ShowFeedbackToggle from '../../_molecules/ShowFeedbackToggle';
import StrengthResponseReviewView from '../../_molecules/StrengthResponseReviewView';
import { Slide } from '../../SlideComponents/slide.types';
import AiFeedbackScoreboard from '../AiFeedbackScoreboard';

const LiveResponsesContext = createContext<Response[] | undefined>(undefined);

const PublicResponsesProvider = ({
  gameId,
  roundId,
  children,
}: {
  gameId: string;
  roundId?: string;
  children: React.ReactNode;
}) => {
  const { responses, isFetching } = useApiResponses(gameId, roundId || 'undefined');

  // Sort responses to show top picks (with strength) first
  const sortedResponses = useMemo(() => {
    if (!responses) return responses;

    return [...responses].sort((a, b) => {
      const aHasStrength = !!a.strength;
      const bHasStrength = !!b.strength;

      // Put responses with strength first
      if (aHasStrength && !bHasStrength) return -1;
      if (!aHasStrength && bHasStrength) return 1;

      // Keep original order for responses in the same category
      return 0;
    });
  }, [responses]);

  return (
    <LiveResponsesContext.Provider value={isFetching ? undefined : sortedResponses}>
      {children}
    </LiveResponsesContext.Provider>
  );
};

const ResponsesProvider = ({
  gameId,
  roundId,
  tenantId,
  children,
}: {
  gameId: string;
  tenantId: string;
  roundId?: string;
  children: React.ReactNode;
}) => {
  const { responses, isLoading } = useResponses(
    gameId,
    roundId || 'undefined',
    true,
    tenantId,
  );

  // Sort responses to show top picks (with strength) first
  const sortedResponses = useMemo(() => {
    if (!responses) return responses;

    return [...responses].sort((a, b) => {
      const aHasStrength = !!a.strength;
      const bHasStrength = !!b.strength;

      // Put responses with strength first
      if (aHasStrength && !bHasStrength) return -1;
      if (!aHasStrength && bHasStrength) return 1;

      // Keep original order for responses in the same category
      return 0;
    });
  }, [responses]);

  return (
    <LiveResponsesContext.Provider value={isLoading ? undefined : sortedResponses}>
      {children}
    </LiveResponsesContext.Provider>
  );
};

// Registers strength-review state and handlers with the central clicker
// provider. Called from both inner grid views (regular + AI). Cleans up
// on unmount so a stale "review open" doesn't outlive navigation away.
// Lives in hooks/ so PodiumView can use it too.

const ResponseGridDataFetcher = ({
  gameId,
  roundId,
  rounds,
  tenantId,
  isAuthenticated,
  children,
  isUsingSockets,
}: {
  rounds: Slide[];
  gameId: string;
  roundId: string;
  tenantId: string;
  isAuthenticated: boolean;
  onRoundWasPlayedTimeout?: () => void;
  children: React.ReactNode;
  isUsingSockets?: boolean;
}) => {
  const responseRound = useMemo(() => {
    const currentRound = rounds.find(r => r.id === roundId);
    if (!currentRound) return undefined;

    const relatedResponseRound = rounds.find(
      r => r.id === currentRound.dataReferenceSlideId,
    );
    return relatedResponseRound;
  }, [rounds, roundId]);

  if (isAuthenticated === false || isUsingSockets === false) {
    return (
      <PublicResponsesProvider gameId={gameId} roundId={responseRound?.id}>
        {children}
      </PublicResponsesProvider>
    );
  }
  return (
    <ResponsesProvider gameId={gameId} tenantId={tenantId} roundId={responseRound?.id}>
      {children}
    </ResponsesProvider>
  );
};

const ResponseGridMainComponent = ({
  gameId,
  roundId,
  rounds,
  tenantId,
  isAuthenticated,
  onRoundWasPlayedTimeout,
  isUsingSockets,
}: {
  rounds: Slide[];
  gameId: string;
  roundId: string;
  tenantId: string;
  isAuthenticated: boolean;
  onRoundWasPlayedTimeout?: () => void;
  isUsingSockets?: boolean;
}) => {
  const [width] = useWindowSize();
  const responseRound = useMemo(() => {
    const currentRound = rounds.find(r => r.id === roundId);
    if (!currentRound) return undefined;
    const relatedResponseRound = rounds.find(
      r => r.id === currentRound.dataReferenceSlideId,
    );
    return relatedResponseRound;
  }, [rounds, roundId]);

  const responsesAsColumns = useCallback(
    (responses: Response[]) => {
      return responses ? makeGridColumns(responses, width) : [];
    },
    [width],
  );

  const [selectedResponse, setSelectedResponse] = useState<Response | undefined>(
    undefined,
  );
  const [selectedImageResponse, setSelectedImageResponse] = useState<
    string | undefined
  >();

  return (
    <ResponseGridDataFetcher
      gameId={gameId}
      roundId={roundId}
      rounds={rounds}
      tenantId={tenantId}
      isAuthenticated={isAuthenticated}
      isUsingSockets={isUsingSockets}
    >
      {(responseRound?.type === 'aiAssessedAnswers' ||
        responseRound?.type === 'aiAssessedImageAnswers') && (
        <AiResponseGridView
          roundId={roundId}
          gameId={gameId}
          tenantId={tenantId}
          isAuthenticated={isAuthenticated}
          onRoundWasPlayedTimeout={onRoundWasPlayedTimeout}
          selectedResponse={selectedResponse}
          setSelectedResponse={setSelectedResponse}
          responseRound={responseRound}
          makeResponsesAsColumns={responsesAsColumns}
          isUsingSockets={isUsingSockets || false}
        />
      )}
      {responseRound?.type !== 'aiAssessedAnswers' &&
        responseRound?.type !== 'aiAssessedImageAnswers' && (
          <ResponseGridView
            roundId={roundId}
            tenantId={tenantId}
            isAuthenticated={isAuthenticated}
            onRoundWasPlayedTimeout={onRoundWasPlayedTimeout}
            selectedResponse={selectedResponse}
            setSelectedResponse={setSelectedResponse}
            selectedImageResponse={selectedImageResponse}
            setSelectedImageResponse={setSelectedImageResponse}
            responseRound={responseRound}
            makeResponsesAsColumns={responsesAsColumns}
            gameId={gameId}
          />
        )}
    </ResponseGridDataFetcher>
  );
};

const ResponseGridView = ({
  roundId,
  tenantId,
  isAuthenticated,
  onRoundWasPlayedTimeout,
  selectedResponse,
  setSelectedResponse,
  selectedImageResponse,
  setSelectedImageResponse,
  responseRound,
  makeResponsesAsColumns,
  gameId,
}: {
  roundId: string;
  tenantId: string;
  isAuthenticated: boolean;
  onRoundWasPlayedTimeout?: () => void;
  selectedResponse: Response | undefined;
  setSelectedResponse: Dispatch<SetStateAction<Response | undefined>>;
  selectedImageResponse: string | undefined;
  setSelectedImageResponse: Dispatch<SetStateAction<string | undefined>>;
  responseRound: Slide | undefined;
  makeResponsesAsColumns: (responses: Response[]) => Response[][];
  gameId: string;
}) => {
  const { t } = useTranslate();
  const { setOpenFirstAiResponseHandler } = useContext(LiveActionsDisabledContext);

  // Store the navigation order when first opening a response
  const [navigationResponses, setNavigationResponses] = useState<Response[] | undefined>(
    undefined,
  );

  const responses = useContext(LiveResponsesContext);

  // Create navigation order once when a response is first selected
  useEffect(() => {
    if (selectedResponse && responses && !navigationResponses) {
      // Find all responses with strength
      const responsesWithStrength = responses.filter(r => !!r.strength);

      // Find other strengths (excluding the currently selected one if it's a strength)
      const otherStrengths = responsesWithStrength.filter(
        r => r.id !== selectedResponse.id,
      );

      // Get all non-strength responses (excluding the selected one)
      const allStrengthIds = new Set(responsesWithStrength.map(r => r.id));
      const nonStrengthResponses = responses.filter(
        r => r.id !== selectedResponse.id && !allStrengthIds.has(r.id),
      );

      // Build the navigation order: current, then other strengths, then non-strengths
      const navOrder = [selectedResponse, ...otherStrengths, ...nonStrengthResponses];
      setNavigationResponses(navOrder);
    }

    if (!selectedResponse) {
      // Clear navigation order when modal is closed
      setNavigationResponses(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResponse, responses]);

  // Keep selectedResponse in sync with the latest server data so updates that
  // arrive while the modal is open (e.g. a strength being detected) appear
  // immediately, without the host having to close and reopen.
  useEffect(() => {
    if (!selectedResponse || !responses) return;
    const fresh = responses.find(r => r.id === selectedResponse.id);
    if (!fresh) return;
    const sameStrength =
      fresh.strength?.description === selectedResponse.strength?.description &&
      fresh.strength?.strengthText === selectedResponse.strength?.strengthText;
    const sameContent = fresh.response === selectedResponse.response;
    if (sameStrength && sameContent) return;
    setSelectedResponse(fresh);
  }, [responses, selectedResponse, setSelectedResponse]);

  useEffect(() => {
    if (!responseRound?.id || !gameId) {
      return;
    }

    const handleOpenFirstResponse = (eventGameId: string, eventSlideId: string) => {
      if (eventGameId !== gameId) return;
      if (eventSlideId !== responseRound.id) return;

      if (responses && responses.length > 0) {
        setSelectedResponse(responses[0]);
      }
    };

    setOpenFirstAiResponseHandler(() => handleOpenFirstResponse);

    return () => {
      setOpenFirstAiResponseHandler(undefined);
    };
  }, [
    gameId,
    responseRound?.id,
    responses,
    setSelectedResponse,
    setOpenFirstAiResponseHandler,
  ]);

  const {
    responseRoundType,
    handleSkipBackResponse,
    handleSkipForwardResponse,
  } = useSwipeResponses({
    selectedResponse,
    slideId: roundId,
    setSelectedResponse,
    gridResponses: navigationResponses || responses,
    slideType: responseRound?.type,
  });

  useStrengthReviewClickerRegistration({
    selectedResponse,
    setSelectedResponse,
    navigationResponses: navigationResponses || responses,
    handleSkipForwardResponse,
    handleSkipBackResponse,
  });

  useAutoMarkAsPlayedResultRound({
    shortTimeout: false,
    callback: onRoundWasPlayedTimeout,
  });

  const responsesAsColumns = useMemo(() => {
    return responses ? makeResponsesAsColumns(responses) : [];
  }, [makeResponsesAsColumns, responses]);

  return (
    <GameActionContainer>
      <>
        <StrengthResponseReviewView
          open={selectedResponse !== undefined}
          response={selectedResponse}
          gameId={gameId}
          isDrawing={responseRound?.type === 'canvas'}
          onDone={() => setSelectedResponse(undefined)}
          isAuthenticated={isAuthenticated}
          tenantId={tenantId}
          onPrevious={handleSkipBackResponse}
          onNext={handleSkipForwardResponse}
          responses={navigationResponses || responses}
          slideId={responseRound?.id}
        />
      </>
      {responses === undefined && <Loader />}
      {responses && responseRoundType === 'canvas' && (
        <Row style={{ width: '90%', flex: 1, marginTop: '20px' }} gutter={[10, 10]}>
          {responses.map(response => {
            return (
              <GridItem
                key={response.id}
                response={response}
                tenantId={tenantId}
                onImageClicked={setSelectedImageResponse}
                isCanvas={responseRoundType === 'canvas'}
                isVideo={false}
                onResponseClicked={setSelectedResponse}
                isAuthenticated={isAuthenticated}
              />
            );
          })}
        </Row>
      )}
      {responses && responseRoundType !== 'canvas' && (
        <ResponseColumnContainer>
          {responsesAsColumns.map((responsesColumn: Response[]) => {
            if (responsesColumn.length > 0) {
              return (
                <ResponseColumn $numberOfColumns={responsesAsColumns.length}>
                  {responsesColumn.map(response => {
                    return (
                      <GridItem
                        key={response.id}
                        response={response}
                        tenantId={tenantId}
                        onImageClicked={setSelectedImageResponse}
                        isCanvas={responseRound?.type === 'canvas'}
                        isVideo={false}
                        onResponseClicked={setSelectedResponse}
                        isAuthenticated={isAuthenticated}
                      />
                    );
                  })}
                </ResponseColumn>
              );
            }
            return;
          })}
        </ResponseColumnContainer>
      )}
      {responses && responses.length === 0 && (
        <LiveEmptyState
          title={t('No responses submitted')}
          style={{ margin: '20px', width: '90%' }}
        />
      )}
    </GameActionContainer>
  );
};

const ResponseColumnContainer = styled.div`
  width: 90%;
  margin: 20px auto;
  display: flex;
  gap: 20px;
`;

const ResponseColumn = styled.div<{
  $numberOfColumns: number;
}>`
  display: flex;
  flex-direction: column;
  flex: 1 1 0px;
  width: ${({ $numberOfColumns }) => 90 / $numberOfColumns}%;
`;

const GridItem = ({
  response,
  onImageClicked,
  isCanvas,
  isVideo,
  isAiAssessedAnswers,
  onResponseClicked,
  tenantId,
  isAuthenticated,
}: {
  response: Response;
  isCanvas: boolean;
  isVideo: boolean;
  isAiAssessedAnswers?: boolean;
  onImageClicked: (imgData: string) => void;
  onResponseClicked: (response: Response) => void;
  tenantId: string;
  isAuthenticated: boolean;
}) => {
  const { t } = useTranslate();
  const [isHiding, setIsHiding] = useState(false);
  const metrics = useMetrics();
  const handleHiding = () => {
    if (isAuthenticated) {
      metrics.logEvent('HideResponse');
    }
    setIsHiding(hidden => !hidden);
  };

  const [isHovering, setIsHovering] = useState(false);

  const hasStrength = response?.strength && response?.strength.description;

  if (!isCanvas && !isVideo) {
    return (
      <TextResponseContainer
        onClick={() => {
          if (isAuthenticated) {
            metrics.logEvent('ResponseGrid.FullScreen.Click', { isCanvas });
          }
          onResponseClicked(response);
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <HideResponseToggle
          open={isHovering}
          isHiding={isHiding}
          onToggle={handleHiding}
        />
        {isAiAssessedAnswers && (
          <ShowFeedbackToggle
            feedbackIconVisible={isHovering}
            onToggle={() => onResponseClicked(response)}
          />
        )}
        {hasStrength && (
          <Tooltip
            title={'This was selected as a great response to discuss as a class'}
            mouseEnterDelay={1}
            placement="top"
          >
            <GreatPickTag>
              <span>{t('greatPick', 'Great Pick')}</span>
              <ProfileChecked />
            </GreatPickTag>
          </Tooltip>
        )}

        <TextResponse>
          <span>{!isHiding ? response.response : ''}</span>
        </TextResponse>
        <FullScreenHoverNudge
          isHovering={isHovering}
          isHiding={isHiding}
          handleMouseDown={() => {
            setIsHovering(false);
          }}
        />
      </TextResponseContainer>
    );
  }

  if (isCanvas) {
    return (
      <Col
        key={response.id}
        xs={24}
        sm={12}
        md={8}
        lg={8}
        xl={8}
        xxl={8}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={() => {
          onResponseClicked(response);
          onImageClicked(response.response);
        }}
        style={{ maxHeight: 300, minHeight: 200 }}
      >
        <GridImageContainer $isHiding={isHiding}>
          <FullScreenHoverNudge
            isHovering={isHovering}
            isHiding={isHiding}
            handleMouseDown={() => {
              setIsHovering(false);
            }}
          />
          <HideResponseToggle
            open={isHovering}
            isHiding={isHiding}
            onToggle={handleHiding}
          />
          {hasStrength && (
            <Tooltip
              title={'This was selected as a great response to discuss as a class'}
              mouseEnterDelay={1}
              placement="top"
            >
              <GreatPickTag>
                <span>{t('greatPick', 'Great Pick')}</span>
                <ProfileChecked />
              </GreatPickTag>
            </Tooltip>
          )}
          {!isHiding && isCanvas && (
            <LazyDrawingResponse
              tenantId={tenantId}
              onClick={() => {
                // Click is handled by parent Col onClick
              }}
              response={response}
            />
          )}
          {!isHiding && isVideo && (
            <LazyVideoResponse
              tenantId={tenantId}
              isAuthenticated={isAuthenticated}
              onClick={() => {
                // Click is handled by parent Col onClick
              }}
              response={response}
            />
          )}
        </GridImageContainer>
      </Col>
    );
  }

  if (isVideo) {
    return (
      <Col
        key={response.id}
        xs={24}
        sm={12}
        md={8}
        lg={8}
        xl={8}
        xxl={8}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        style={{ maxHeight: 300, minHeight: 200 }}
      >
        <div
          style={{
            height: '100%',
            width: '100%',
            position: 'relative',
          }}
        >
          {isHovering && (
            <IconWrapper
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                backgroundColor: theme.colors.greyLight,
                padding: '5px',
                borderRadius: '10px',
                transform: 'translate(-50%, -50%)',
                opacity: 0.9,
              }}
              color={theme.colors.black}
              height={'20px'}
              width={'20px'}
            >
              <FullScreenSvg />
            </IconWrapper>
          )}
          <HideResponseToggle
            open={isHovering}
            isHiding={isHiding}
            onToggle={handleHiding}
          />
          {!isHiding && isVideo && (
            <LazyVideoResponse
              tenantId={tenantId}
              isAuthenticated={isAuthenticated}
              onClick={imgUrl => {
                onImageClicked(imgUrl);
                onResponseClicked(response);
              }}
              response={response}
            />
          )}
        </div>
      </Col>
    );
  }

  return null;
};

const AiResponseGridView = ({
  roundId,
  gameId,
  tenantId,
  isAuthenticated,
  onRoundWasPlayedTimeout,
  selectedResponse,
  setSelectedResponse,
  responseRound,
  makeResponsesAsColumns,
  isUsingSockets,
}: {
  roundId: string;
  gameId: string;
  tenantId: string;
  isAuthenticated: boolean;
  onRoundWasPlayedTimeout?: () => void;
  selectedResponse: Response | undefined;
  setSelectedResponse: Dispatch<SetStateAction<Response | undefined>>;
  responseRound: Slide | undefined;
  makeResponsesAsColumns: (responses: Response[]) => Response[][];
  isUsingSockets: boolean;
}) => {
  const { t } = useTranslate();
  const responseRoundId = responseRound?.id;
  const { setShowMediaIsDisabled } = useContext(LiveActionsDisabledContext);

  // Store the navigation order when first opening a response
  const [navigationResponses, setNavigationResponses] = useState<Response[] | undefined>(
    undefined,
  );

  const {
    aiAssessedScores,
    isFetchingAiAssessedScores,
    isGeneratingNewScores,
  } = useListAllAiAssessedScores({
    gameId,
    tenantId,
    slideId: responseRoundId,
    isUsingSockets,
    setShowMediaIsDisabled,
  });

  const aiAssessedScore = useMemo(() => {
    if (aiAssessedScores && selectedResponse?.id) {
      const aiAssessedScore = aiAssessedScores.find(
        s => s.responseId === selectedResponse?.id,
      );
      return aiAssessedScore;
    }
    return undefined;
  }, [aiAssessedScores, selectedResponse, responseRound]);

  const showScoreboard = useMemo(() => {
    if (!responseRound) return false;
    if (responseRound.maxScore == null) return false;
    return hasScorePromptOutput(responseRound);
  }, [responseRound]);

  const [scoreboardView, setScoreboardView] = useState<'scoreboard' | 'grid'>(
    showScoreboard ? 'scoreboard' : 'grid',
  );

  // useLayoutEffect (not useEffect) so the view state syncs before the browser
  // paints — otherwise the back-to-scoreboard button briefly flashes on top of
  // the response grid before the scoreboard takes over on initial data load.
  useLayoutEffect(() => {
    setScoreboardView(showScoreboard ? 'scoreboard' : 'grid');
  }, [showScoreboard, responseRound?.id]);

  // When the host advances from the scoreboard into the strength review,
  // pre-switch to the grid view so closing the modal lands on the response
  // grid (step 6 of the flow) instead of back on the scoreboard.
  useEffect(() => {
    if (selectedResponse && showScoreboard) {
      setScoreboardView('grid');
    }
  }, [selectedResponse, showScoreboard]);

  // Per product spec: every assessed response contributes to the chart. If
  // aiScore is missing (null / undefined / non-number), treat it as 0 — the
  // response was scored, just not numerically resolvable.
  const scoreboardScores = useMemo(() => {
    if (!aiAssessedScores) return [];
    return aiAssessedScores.map(a =>
      typeof a.aiScore === 'number' && Number.isFinite(a.aiScore) ? a.aiScore : 0,
    );
  }, [aiAssessedScores]);

  const responses = useContext(LiveResponsesContext);

  // Top-3 navigation order for scoreboard activities, sorted by aiScore
  // desc. Drives both the strength-review walk AND the "open first
  // response" handler so the host opens straight onto the top scorer.
  // Undefined when showScoreboard is off — the legacy strength-first
  // nav-order branch below takes over in that case.
  const topScoredResponses = useMemo(() => {
    if (!showScoreboard) return undefined;
    if (!responses || responses.length === 0) return undefined;
    if (!aiAssessedScores) return undefined;
    const scoreByResponseId = new Map(
      aiAssessedScores.map(s => [
        s.responseId,
        typeof s.aiScore === 'number' && Number.isFinite(s.aiScore) ? s.aiScore : 0,
      ]),
    );
    return responses
      .map(r => ({ response: r, score: scoreByResponseId.get(r.id) ?? 0 }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map(s => s.response);
  }, [showScoreboard, responses, aiAssessedScores]);

  // Create navigation order once when a response is first selected
  useEffect(() => {
    if (selectedResponse && responses && !navigationResponses) {
      // Scoreboard flow: navigate strictly through the top-3 sorted set,
      // keeping the currently selected response first so closing the
      // modal lands on the response the host opened from.
      if (topScoredResponses && topScoredResponses.length > 0) {
        const others = topScoredResponses.filter(r => r.id !== selectedResponse.id);
        setNavigationResponses([selectedResponse, ...others]);
        return;
      }

      // Find all responses with strength
      const responsesWithStrength = responses.filter(r => !!r.strength);

      // Find other strengths (excluding the currently selected one if it's a strength)
      const otherStrengths = responsesWithStrength.filter(
        r => r.id !== selectedResponse.id,
      );

      // Get all non-strength responses (excluding the selected one)
      const allStrengthIds = new Set(responsesWithStrength.map(r => r.id));
      const nonStrengthResponses = responses.filter(
        r => r.id !== selectedResponse.id && !allStrengthIds.has(r.id),
      );

      // Build the navigation order: current, then other strengths, then non-strengths
      const navOrder = [selectedResponse, ...otherStrengths, ...nonStrengthResponses];
      setNavigationResponses(navOrder);
    }

    if (!selectedResponse) {
      // Clear navigation order when modal is closed
      setNavigationResponses(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResponse, responses, topScoredResponses]);

  // Keep selectedResponse in sync with the latest server data so updates that
  // arrive while the modal is open (e.g. a strength being detected) appear
  // immediately, without the host having to close and reopen.
  useEffect(() => {
    if (!selectedResponse || !responses) return;
    const fresh = responses.find(r => r.id === selectedResponse.id);
    if (!fresh) return;
    const sameStrength =
      fresh.strength?.description === selectedResponse.strength?.description &&
      fresh.strength?.strengthText === selectedResponse.strength?.strengthText;
    const sameContent = fresh.response === selectedResponse.response;
    if (sameStrength && sameContent) return;
    setSelectedResponse(fresh);
  }, [responses, selectedResponse, setSelectedResponse]);

  const { handleSkipBackResponse, handleSkipForwardResponse } = useSwipeResponses({
    selectedResponse,
    slideId: roundId,
    setSelectedResponse,
    gridResponses: navigationResponses || responses,
    slideType: responseRound?.type,
  });

  useStrengthReviewClickerRegistration({
    selectedResponse,
    setSelectedResponse,
    navigationResponses: navigationResponses || responses,
    handleSkipForwardResponse,
    handleSkipBackResponse,
  });

  useAutoMarkAsPlayedResultRound({
    shortTimeout: true,
    callback: onRoundWasPlayedTimeout,
  });

  const responsesAsColumns = useMemo(() => {
    return responses ? makeResponsesAsColumns(responses) : [];
  }, [makeResponsesAsColumns, responses]);

  // Register handler in context to open first response (called by SimpleLiveGameWidgetV2)
  const { setOpenFirstAiResponseHandler } = useContext(LiveActionsDisabledContext);

  useEffect(() => {
    if (!responseRound?.id || !gameId) {
      return;
    }

    const handleOpenFirstResponse = (eventGameId: string, eventSlideId: string) => {
      if (eventGameId !== gameId) return;
      if (eventSlideId !== responseRound.id) return;

      // Scoreboard activities open the highest scorer first so the
      // strength walk follows the same ranking the scoreboard just
      // animated. Everything else falls back to the natural response
      // order.
      const first =
        topScoredResponses && topScoredResponses.length > 0
          ? topScoredResponses[0]
          : responses && responses.length > 0
          ? responses[0]
          : undefined;
      if (first) {
        setSelectedResponse(first);
      }
    };

    setOpenFirstAiResponseHandler(() => handleOpenFirstResponse);

    return () => {
      setOpenFirstAiResponseHandler(undefined);
    };
  }, [
    gameId,
    responseRound?.id,
    responses,
    topScoredResponses,
    setSelectedResponse,
    setOpenFirstAiResponseHandler,
  ]);

  return (
    <GameActionContainer>
      <AIResponseReviewView
        open={selectedResponse !== undefined}
        response={selectedResponse}
        gameId={gameId}
        aiAssessedScore={aiAssessedScore}
        isDrawing={responseRound?.type === 'aiAssessedImageAnswers'}
        isLoadingScores={isFetchingAiAssessedScores || isGeneratingNewScores}
        onDone={() => setSelectedResponse(undefined)}
        isAuthenticated={isAuthenticated}
        tenantId={tenantId}
        onPrevious={handleSkipBackResponse}
        onNext={handleSkipForwardResponse}
        responses={navigationResponses || responses}
        slideId={responseRound?.id}
        slide={responseRound}
      />

      {showScoreboard && scoreboardView === 'scoreboard' && responseRound && (
        <AiFeedbackScoreboard
          // Key on the activity slide id so navigating to a different
          // scoreboard slide unmounts the previous instance and re-runs the
          // fill animation from 0%.
          key={responseRound.id}
          scores={scoreboardScores}
          maxScore={responseRound.maxScore ?? -1}
          totalStudents={responses?.length ?? 0}
          isScoring={isFetchingAiAssessedScores || isGeneratingNewScores}
          onSeeAllResponses={() => setScoreboardView('grid')}
        />
      )}

      {showScoreboard && scoreboardView === 'grid' && (
        <BackToScoreboardButton onClick={() => setScoreboardView('scoreboard')}>
          <T keyName="Scoreboard.backToScoreboard" defaultValue="← Scoreboard" />
        </BackToScoreboardButton>
      )}

      {(!showScoreboard || scoreboardView === 'grid') && responses === undefined && (
        <Loader />
      )}
      {(!showScoreboard || scoreboardView === 'grid') &&
        responses &&
        responseRound &&
        responseRound.type === 'aiAssessedImageAnswers' && (
          <Row style={{ width: '90%', flex: 1, marginTop: '20px' }} gutter={[10, 10]}>
            {responses.map(response => {
              return (
                <GridItem
                  key={response.id}
                  response={response}
                  tenantId={tenantId}
                  onImageClicked={() => {
                    //TODO Not used remove
                  }}
                  isCanvas={true}
                  isVideo={false}
                  onResponseClicked={setSelectedResponse}
                  isAuthenticated={isAuthenticated}
                />
              );
            })}
          </Row>
        )}
      {(!showScoreboard || scoreboardView === 'grid') &&
        responses &&
        responseRound &&
        responseRound.type === 'aiAssessedAnswers' && (
          <ResponseColumnContainer>
            {responsesAsColumns.map(responsesColumn => {
              if (responsesColumn.length > 0) {
                return (
                  <ResponseColumn $numberOfColumns={responsesAsColumns.length}>
                    {responsesColumn.map(response => {
                      return (
                        <AiAssessedGridItem
                          key={response.id}
                          response={response}
                          onResponseClicked={setSelectedResponse}
                          isAuthenticated={isAuthenticated}
                        />
                      );
                    })}
                  </ResponseColumn>
                );
              }
              return;
            })}
          </ResponseColumnContainer>
        )}
      {(!showScoreboard || scoreboardView === 'grid') &&
        responses &&
        responses.length === 0 && (
          <LiveEmptyState
            title={t('No responses submitted')}
            style={{ margin: '20px', width: '90%' }}
          />
        )}
    </GameActionContainer>
  );
};

const BackToScoreboardButton = styled.button`
  position: fixed;
  top: 16px;
  left: 16px;
  background: #f8e6d3;
  border: 2px solid #1a1f18;
  color: #1a1f18;
  font-family: 'Nunito', sans-serif;
  font-size: 14px;
  font-weight: 900;
  border-radius: 999px;
  padding: 8px 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  z-index: ${zIndexes('AI_FEEDBACK_OVERLAY')};
  transition: all 0.18s cubic-bezier(0.22, 1, 0.36, 1);

  &:hover {
    background: #1a1f18;
    color: #fffcf5;
    transform: translateY(-1px);
  }
`;

const AiAssessedGridItem = ({
  response,
  onResponseClicked,
  isAuthenticated,
}: {
  response: Response;
  onResponseClicked: (response: Response) => void;
  isAuthenticated: boolean;
}) => {
  const { t } = useTranslate();
  const [isHiding, setIsHiding] = useState(false);
  const metrics = useMetrics();

  const handleHiding = () => {
    if (isAuthenticated) {
      metrics.logEvent('HideResponse');
    }
    setIsHiding(hidden => !hidden);
  };

  const [isHovering, setIsHovering] = useState(false);

  const hasStrength = response?.strength && response?.strength.description;

  return (
    <TextResponseContainer
      onClick={() => {
        onResponseClicked(response);
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <HideResponseToggle open={isHovering} isHiding={isHiding} onToggle={handleHiding} />
      <ShowFeedbackToggle
        feedbackIconVisible={isHovering}
        onToggle={() => onResponseClicked(response)}
      />
      {hasStrength && (
        <Tooltip
          title={'This was selected as a great response to discuss as a class'}
          mouseEnterDelay={1}
          placement="top"
        >
          <GreatPickTag>
            <span>{t('greatPick', 'Great Pick')}</span>
            <ProfileChecked />
          </GreatPickTag>
        </Tooltip>
      )}
      <TextResponse>
        <span>{!isHiding ? response.response : ''}</span>
      </TextResponse>
      <FullScreenHoverNudge
        isHovering={isHovering}
        isHiding={isHiding}
        handleMouseDown={() => {
          setIsHovering(false);
        }}
      />
    </TextResponseContainer>
  );
};

const GridImageContainer = styled.div<{ $isHiding: boolean }>`
  height: 100%;
  width: 100%;
  position: relative;
  background: white;
  border-radius: 20px;
  &:hover {
    filter: brightness(95%);
  }
  box-shadow: ${({ $isHiding }) =>
    !$isHiding ? '0px 2px 20px rgba(0,0,0,0.15)' : 'none'};
`;

const TextResponseContainer = styled.div`
  cursor: pointer;
  height: max-content;
  width: 100%;
  margin: 10px 0px;
  padding-top: 10px;
  position: relative;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.15);
  border-bottom: 5px solid ${theme.colors.green};
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
  display: inline-block;
  &:hover {
    background: ${theme.colors.greyLight};
  }
`;

const GreatPickTag = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  background-color: ${theme.colors.betaYellow};
  color: ${theme.colors.white};
  padding: 6px 12px;
  border-radius: 10px 0px 10px 0px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 600;
  z-index: 2;

  svg {
    width: 14px;
    height: 14px;
    color: ${theme.colors.white};
  }

  span {
    line-height: 1;
  }
`;

const TextResponse = styled.div<{ $isFeedback?: boolean }>`
  padding: 20px;
  text-align: center;
  flex: 1;
  width: 100%;
  & span {
    width: 100%;
    text-align: left;
    font-size: 24px;
    font-weight: bold;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ $isFeedback }) =>
    $isFeedback &&
    css`
      position: absolute;
      height: 100%;
      top: 0px;
      left: 0px;
      background: ${theme.colors.white};
      border-radius: 10px;
      &:hover {
        background: ${theme.colors.greyLight};
      }
    `}
`;

export default ResponseGridMainComponent;

const GameActionContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding-bottom: 50px;
  width: 100%;
`;
